import type {
  PatientDetail,
  UpdatePatientInput,
} from '@/features/patients/types';

/**
 * Email + address part of "Edit details" (UAT RL-1042) and its mapping to
 * `PATCH /patients/:id` (UpdatePatientDto). Only changed fields are sent; a
 * blank address field clears it (the API stores "" as null). The API cannot
 * remove an email (it accepts only a valid address), so clearing one is refused
 * here with an explanation instead of failing on save.
 */
export interface ContactForm {
  email: string;
  addressLine1: string;
  addressLine2: string;
  postcode: string;
  city: string;
  state: string;
}

type AddressKey = Exclude<keyof ContactForm, 'email'>;
const ADDRESS_KEYS: AddressKey[] = [
  'addressLine1',
  'addressLine2',
  'postcode',
  'city',
  'state',
];

/** Same limits as the API DTO (AddressFieldsDto, email @MaxLength(254)). */
export const CONTACT_LIMITS: Record<keyof ContactForm, number> = {
  email: 254,
  addressLine1: 160,
  addressLine2: 160,
  postcode: 10,
  city: 80,
  state: 80,
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function toContactForm(p: PatientDetail): ContactForm {
  return {
    email: p.email ?? '',
    addressLine1: p.address.line1 ?? '',
    addressLine2: p.address.line2 ?? '',
    postcode: p.address.postcode ?? '',
    city: p.address.city ?? '',
    state: p.address.state ?? '',
  };
}

export function buildContactPatch(
  p: PatientDetail,
  form: ContactForm,
): {
  patch: Pick<UpdatePatientInput, keyof ContactForm>;
  errors: Partial<Record<keyof ContactForm, string>>;
} {
  const before = toContactForm(p);
  const patch: Pick<UpdatePatientInput, keyof ContactForm> = {};
  const errors: Partial<Record<keyof ContactForm, string>> = {};

  const email = form.email.trim();
  if (email !== before.email) {
    if (!email)
      errors.email =
        'An email cannot be removed here, only changed. Enter the new email or keep the old one.';
    else if (!EMAIL.test(email) || email.length > CONTACT_LIMITS.email)
      errors.email = 'Enter a valid email, e.g. name@example.com.';
    else if (email.toLowerCase() !== before.email.toLowerCase())
      patch.email = email;
  }

  for (const key of ADDRESS_KEYS) {
    const value = form[key].trim();
    if (value.length > CONTACT_LIMITS[key]) {
      errors[key] = `At most ${CONTACT_LIMITS[key]} characters.`;
      continue;
    }
    if (value !== before[key]) patch[key] = value;
  }
  return { patch, errors };
}
